// 週排程（SPEC #ls2505-334「偵測排程」、ADR-004 §2.5）：codec + 編輯器的塗格 / 範本。
//
// wire：7 個 48 字元 '0' / '1' 字串，index 0 = 週日、半小時一格、設備時區（component/protocol shapes.go Schedule）。
// 用在 kinio_motion.schedule，以及同一個編輯器的其他週排程屬性（kinio_recording.*schedule、kinio_analytics.schedule、
// kinio_floodlight.schedule、kinio_siren.schedule）。畫面顯示的週首日依使用者語系，寫出一律週日起。

export const SCHEDULE_DAYS = 7;
export const SCHEDULE_SLOTS = 48;

/** [day][slot]，day 0 = 週日。 */
export type ScheduleGrid = boolean[][];

export type ScheduleTemplate = 'all_day' | 'night' | 'office' | 'clear';

const DAY_RE = /^[01]{48}$/;

/** wire → 格子；不是 7 個 48 字元的 '0'/'1' 字串 → null（整個值不收，不硬修）。 */
export function parseSchedule(raw: unknown): ScheduleGrid | null {
  if (!Array.isArray(raw) || raw.length !== SCHEDULE_DAYS) return null;
  const out: ScheduleGrid = [];
  for (const d of raw) {
    if (typeof d !== 'string' || !DAY_RE.test(d)) return null;
    out.push([...d].map((c) => c === '1'));
  }
  return out;
}

export function encodeSchedule(grid: ScheduleGrid): string[] {
  return grid.map((day) => day.map((on) => (on ? '1' : '0')).join(''));
}

export function emptySchedule(): ScheduleGrid {
  return Array.from({ length: SCHEDULE_DAYS }, () => Array<boolean>(SCHEDULE_SLOTS).fill(false));
}

export function scheduleEqual(a: ScheduleGrid, b: ScheduleGrid): boolean {
  return a.length === b.length && a.every((d, i) => d.length === b[i].length && d.every((v, j) => v === b[i][j]));
}

/** 收斂後判斷設備是否原樣採用（`adopted()` 用 === 比不了陣列）。 */
export function scheduleConverged(reported: unknown, sent: unknown): boolean {
  const r = parseSchedule(reported);
  const s = parseSchedule(sent);
  return r !== null && s !== null && scheduleEqual(r, s);
}

/** 摘要：全天 / 不啟用 / 自訂；解析不了 → null。 */
export function scheduleSummary(raw: unknown): 'all_day' | 'off' | 'custom' | null {
  const g = parseSchedule(raw);
  if (!g) return null;
  const flat = g.flat();
  if (flat.every(Boolean)) return 'all_day';
  if (!flat.some(Boolean)) return 'off';
  return 'custom';
}

/** 範本（SPEC：全天、夜間 18:00–06:00、上班時間 週一到週五 09:00–18:00、清除）。 */
export function scheduleTemplate(t: ScheduleTemplate): ScheduleGrid {
  const g = emptySchedule();
  for (let d = 0; d < SCHEDULE_DAYS; d++) {
    for (let s = 0; s < SCHEDULE_SLOTS; s++) {
      if (t === 'all_day') g[d][s] = true;
      else if (t === 'night') g[d][s] = s < 12 || s >= 36; // 00:00–06:00 與 18:00–24:00
      else if (t === 'office') g[d][s] = d >= 1 && d <= 5 && s >= 18 && s < 36; // 週一到週五 09:00–18:00
    }
  }
  return g;
}

/**
 * 拖曳塗格：起點那格的反相決定這一筆是塗或擦（同網格規則）；起點到目前點圍成的矩形全部設成那個值。
 * `base` 是按下前的格子（拖回來時縮小的範圍會還原）。
 */
export function paintRect(base: ScheduleGrid, from: { day: number; slot: number }, to: { day: number; slot: number }): ScheduleGrid {
  const value = !base[from.day]?.[from.slot];
  const d0 = Math.max(0, Math.min(from.day, to.day));
  const d1 = Math.min(SCHEDULE_DAYS - 1, Math.max(from.day, to.day));
  const s0 = Math.max(0, Math.min(from.slot, to.slot));
  const s1 = Math.min(SCHEDULE_SLOTS - 1, Math.max(from.slot, to.slot));
  return base.map((day, d) => day.map((on, s) => (d >= d0 && d <= d1 && s >= s0 && s <= s1 ? value : on)));
}

/** 畫面上的列順序（週首日 0 = 週日、1 = 週一）；值是 wire 的 day index。 */
export function scheduleDayOrder(weekStart: number): number[] {
  const ws = ((weekStart % 7) + 7) % 7;
  return Array.from({ length: SCHEDULE_DAYS }, (_, i) => (ws + i) % 7);
}

/** 第幾格的開始時間 HH:MM。 */
export function slotLabel(slot: number): string {
  const m = slot * 30;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
